import React, { useRef, useEffect, useState, useCallback } from 'react';

/**
 * LiveKitAvatarPanel
 * ==================
 * Fixed bottom-left panel showing the LiveKit avatar video stream
 * with presentation controls. Dark/gold theme matching the deck.
 */

const Z = {
  bg: '#FFFFFF',
  bgCard: '#FFFFFF',
  bgCardHover: '#F3F4F6',
  gold: '#21436b',
  goldLight: '#2a5080',
  goldDark: '#1a365d',
  heading: '#21436b',
  white: '#FFFFFF',
  gray400: 'rgba(33,67,107,0.7)',
  gray600: 'rgba(33,67,107,0.55)',
  border: 'rgba(33,67,107,0.12)',
  borderSubtle: '#e6edf1',
};

const LiveKitAvatarPanel = ({ isActive, hook, onClose, onSlideAdvance, avatarName, avatarImageUrl, mode = 'button' }) => {
  const name = avatarName || 'AI Presenter';
  const videoRef = useRef(null);
  const audioRef = useRef(null);
  const panelRef = useRef(null);
  const dragState = useRef({ isDragging: false, startX: 0, startY: 0, startLeft: 0, startTop: 0 });
  const [position, setPosition] = useState({ left: 24, top: null, bottom: 96 });

  const handleDragStart = useCallback((clientX, clientY) => {
    const el = panelRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    dragState.current = {
      isDragging: true,
      startX: clientX,
      startY: clientY,
      startLeft: rect.left,
      startTop: rect.top,
    };
  }, []);

  const handleDragMove = useCallback((clientX, clientY) => {
    if (!dragState.current.isDragging) return;
    const { startX, startY, startLeft, startTop } = dragState.current;
    const newLeft = Math.max(0, Math.min(window.innerWidth - 360, startLeft + (clientX - startX)));
    const newTop = Math.max(0, Math.min(window.innerHeight - 100, startTop + (clientY - startY)));
    setPosition({ left: newLeft, top: newTop, bottom: null });
  }, []);

  const handleDragEnd = useCallback(() => {
    dragState.current.isDragging = false;
  }, []);

  useEffect(() => {
    const onMouseMove = (e) => handleDragMove(e.clientX, e.clientY);
    const onMouseUp = () => handleDragEnd();
    const onTouchMove = (e) => { if (e.touches[0]) handleDragMove(e.touches[0].clientX, e.touches[0].clientY); };
    const onTouchEnd = () => handleDragEnd();

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    window.addEventListener('touchmove', onTouchMove, { passive: true });
    window.addEventListener('touchend', onTouchEnd);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onTouchEnd);
    };
  }, [handleDragMove, handleDragEnd]);

  // Attach/detach video track
  useEffect(() => {
    const el = videoRef.current;
    if (hook.videoTrack && el) {
      hook.videoTrack.attach(el);
      return () => hook.videoTrack.detach(el);
    }
  }, [hook.videoTrack]);

  // Attach/detach audio track
  useEffect(() => {
    if (hook.audioTrack) {
      const audioEl = audioRef.current || hook.audioTrack.attach();
      audioRef.current = audioEl;
      audioEl.volume = 1.0;
      audioEl.play().catch(() => {});
      return () => {
        hook.audioTrack.detach(audioEl);
        audioRef.current = null;
      };
    }
  }, [hook.audioTrack]);

  // Register slide advance callback
  useEffect(() => {
    if (onSlideAdvance) {
      hook.setOnSlideAdvance(onSlideAdvance);
    }
    return () => hook.setOnSlideAdvance(null);
  }, [onSlideAdvance, hook]);

  const isQAMode = hook.agentState === 'qa' || hook.agentState === 'presentation_complete';
  const activity = hook.agentActivity || 'idle';
  const isAgentActive = ['presenting', 'qa', 'qa_loading', 'qa_pending', 'presentation_complete'].includes(hook.agentState);

  const stateLabel =
    hook.agentState === 'ended' ? 'Session Ended' :
    hook.agentState === 'audio_only' ? 'Audio Only' :
    hook.agentState === 'presentation_complete' ? 'Q&A' :
    hook.agentState === 'qa_loading' ? 'Loading Q&A...' :
    hook.agentState === 'qa' ? 'Q&A' :
    hook.agentState === 'qa_pending' ? 'Hand Raised' :
    mode === 'interactive' ? 'Presenting' :
    hook.agentState === 'presenting' ? 'Presenting' :
    'Connecting';

  // In QA mode, show activity-aware hints
  const qaActivityHint =
    activity === 'speaking' ? `${name} is speaking...` :
    activity === 'thinking' ? `${name} is thinking...` :
    `Ask your question — ${name} is listening`;

  const footerHint =
    hook.agentState === 'ended' ? 'Session has ended' :
    hook.agentState === 'audio_only' ? 'Presenter unavailable — audio presentation active' :
    mode === 'interactive' ? `${name} is Presenting` :
    hook.agentState === 'presenting' ? `${name} is presenting your proposal` :
    hook.agentState === 'qa_pending' ? 'Waiting for current slide to finish...' :
    hook.agentState === 'presentation_complete' ? (activity === 'speaking' ? `${name} is speaking...` : activity === 'thinking' ? `${name} is thinking...` : 'All slides presented — ask any questions') :
    hook.agentState === 'qa_loading' ? `${name} is getting ready...` :
    hook.agentState === 'qa' ? qaActivityHint :
    hook.isConnecting ? 'Connecting to presenter...' :
    'Starting up...';

  return (
    <div
      ref={panelRef}
      className="fixed z-50"
      style={{
        left: position.left,
        ...(position.top != null ? { top: position.top } : { bottom: position.bottom }),
        opacity: isActive ? 1 : 0,
        transform: isActive ? 'translateY(0)' : 'translateY(20px)',
        transition: dragState.current.isDragging ? 'none' : 'opacity 0.4s ease-out, transform 0.4s ease-out',
        pointerEvents: isActive ? 'auto' : 'none',
      }}
    >
      <div
        className="rounded-2xl overflow-hidden"
        style={{
          background: Z.bgCard,
          border: `1px solid ${Z.gold}`,
          boxShadow: `0 4px 20px rgba(0,0,0,0.1), 0 0 0 1px ${Z.border}`,
          width: 360,
        }}
      >
        {/* Header — drag handle */}
        <div
          className="flex items-center justify-between px-4 py-3"
          style={{ borderBottom: `1px solid ${Z.borderSubtle}`, cursor: 'grab', userSelect: 'none' }}
          onMouseDown={(e) => { e.preventDefault(); handleDragStart(e.clientX, e.clientY); }}
          onTouchStart={(e) => { if (e.touches[0]) handleDragStart(e.touches[0].clientX, e.touches[0].clientY); }}
        >
          <div className="flex items-center gap-2">
            {/* Pulsing indicator */}
            <div
              className="w-2 h-2 rounded-full"
              style={{
                background: (hook.agentState === 'presenting' && mode === 'interactive') ? (
                    activity === 'speaking' ? Z.gold :
                    activity === 'thinking' ? Z.gold :
                    '#22C55E'
                  ) :
                  hook.agentState === 'presenting' ? '#22C55E' :
                  hook.agentState === 'qa_loading' ? Z.gold :
                  isQAMode && activity === 'speaking' ? Z.gold :
                  isQAMode && activity === 'thinking' ? Z.gold :
                  isQAMode ? '#22C55E' :
                  hook.agentState === 'qa_pending' ? Z.gold : Z.gray400,
                boxShadow: (hook.agentState === 'presenting' || (isQAMode && activity === 'listening')) ? '0 0 6px rgba(34,197,94,0.5)' : 'none',
                animation: (hook.agentState === 'presenting' || hook.agentState === 'qa_pending' || hook.agentState === 'qa_loading' || (isQAMode && activity === 'speaking')) ? 'lkPulse 2s ease-in-out infinite' : 'none',
              }}
            />
            <span className="text-sm font-semibold" style={{ color: Z.heading }}>
              {name}
            </span>
            <span className="text-xs" style={{ color: Z.gray400 }}>
              — {stateLabel}
            </span>
          </div>
          <button
            onClick={onClose}
            className="w-7 h-7 rounded-full flex items-center justify-center transition-colors hover:bg-white/10"
            style={{ color: Z.gray400 }}
            title="Close presenter"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Video area */}
        <div
          className="relative flex items-center justify-center"
          style={{ width: '100%', height: 220, background: '#000' }}
        >
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted={false}
            className="w-full h-full object-cover"
            style={{
              display: hook.videoTrack ? 'block' : 'none',
              objectPosition: 'center 25%',
            }}
          />

          {/* Audio-only state */}
          {!hook.videoTrack && !hook.error && hook.agentState === 'audio_only' && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
              <div
                className="w-14 h-14 rounded-full flex items-center justify-center"
                style={{ background: `${Z.gold}20`, animation: 'lkPulse 2s ease-in-out infinite' }}
              >
                <svg className="w-7 h-7" style={{ color: Z.gold }} fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19.114 5.636a9 9 0 010 12.728M16.463 8.288a5.25 5.25 0 010 7.424M6.75 8.25l4.72-4.72a.75.75 0 011.28.53v15.88a.75.75 0 01-1.28.53l-4.72-4.72H4.51c-.88 0-1.704-.507-1.938-1.354A9.01 9.01 0 012.25 12c0-.83.112-1.633.322-2.396C2.806 8.756 3.63 8.25 4.51 8.25H6.75z" />
                </svg>
              </div>
              <span className="text-xs font-medium" style={{ color: Z.heading }}>Audio Presentation</span>
              <span className="text-[10px]" style={{ color: Z.gray400 }}>Presenter video unavailable</span>
            </div>
          )}

          {/* Loading state */}
          {!hook.videoTrack && !hook.error && hook.agentState !== 'audio_only' && hook.agentState !== 'ended' && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
              <div
                className="w-10 h-10 rounded-full border-2 border-t-transparent animate-spin"
                style={{ borderColor: `${Z.gold} transparent ${Z.gold} ${Z.gold}` }}
              />
              <span className="text-xs" style={{ color: Z.gray400 }}>
                {hook.isConnecting ? 'Connecting...' : 'Waiting for presenter...'}
              </span>
            </div>
          )}

          {/* Session ended state */}
          {hook.agentState === 'ended' && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-4">
              <div
                className="w-12 h-12 rounded-full flex items-center justify-center"
                style={{ background: `${Z.gold}20` }}
              >
                <svg className="w-6 h-6" style={{ color: Z.gold }} fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <span className="text-sm font-medium text-center" style={{ color: Z.heading }}>
                Session Ended
              </span>
              <span className="text-xs text-center" style={{ color: Z.gray400 }}>
                The presentation session has ended
              </span>
              <button
                onClick={hook.restart}
                className="mt-1 flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-semibold transition-all hover:scale-105"
                style={{
                  background: `linear-gradient(135deg, ${Z.gold}, ${Z.goldDark})`,
                  color: '#fff',
                }}
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182" />
                </svg>
                Restart Presentation
              </button>
            </div>
          )}

          {/* QA Loading transition overlay */}
          {hook.agentState === 'qa_loading' && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3" style={{ background: 'rgba(0,0,0,0.75)', zIndex: 10 }}>
              <div
                className="w-12 h-12 rounded-full border-2 border-t-transparent animate-spin"
                style={{ borderColor: `${Z.gold} transparent ${Z.gold} ${Z.gold}` }}
              />
              <span className="text-sm font-medium" style={{ color: Z.heading }}>
                Switching to Q&A
              </span>
              <span className="text-xs" style={{ color: Z.gray400 }}>
                {name} is getting ready...
              </span>
            </div>
          )}

          {/* Activity indicator badge — QA mode (button), or interactive once agent is active */}
          {(isQAMode || (mode === 'interactive' && isAgentActive && activity !== 'idle')) && (
            <div
              className="absolute top-2 right-2 flex items-center gap-1.5 px-2 py-1 rounded-full"
              style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)', zIndex: 5 }}
            >
              {activity === 'speaking' && (
                <>
                  <div className="flex items-center gap-0.5">
                    <div className="w-1 h-3 rounded-full" style={{ background: Z.gold, animation: 'lkBar 0.6s ease-in-out infinite' }} />
                    <div className="w-1 h-4 rounded-full" style={{ background: Z.gold, animation: 'lkBar 0.6s ease-in-out 0.15s infinite' }} />
                    <div className="w-1 h-2 rounded-full" style={{ background: Z.gold, animation: 'lkBar 0.6s ease-in-out 0.3s infinite' }} />
                  </div>
                  <span className="text-[10px] font-medium" style={{ color: Z.gold }}>Speaking</span>
                </>
              )}
              {activity === 'thinking' && (
                <>
                  <div className="w-2.5 h-2.5 rounded-full border border-t-transparent animate-spin" style={{ borderColor: `${Z.gold} transparent ${Z.gold} ${Z.gold}` }} />
                  <span className="text-[10px] font-medium" style={{ color: Z.gold }}>Thinking</span>
                </>
              )}
              {activity === 'listening' && (
                <>
                  <div className="w-2 h-2 rounded-full" style={{ background: '#22C55E', animation: 'lkPulse 2s ease-in-out infinite' }} />
                  <span className="text-[10px] font-medium" style={{ color: '#22C55E' }}>Listening</span>
                </>
              )}
            </div>
          )}

          {/* Error state */}
          {hook.error && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 px-4">
              <svg className="w-8 h-8" style={{ color: '#EF4444' }} fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
              </svg>
              <span className="text-xs text-center" style={{ color: '#EF4444' }}>
                {hook.error}
              </span>
            </div>
          )}
        </div>

        {/* Controls bar */}
        <div
          className="px-4 py-3 flex items-center justify-center"
          style={{ borderTop: `1px solid ${Z.borderSubtle}` }}
        >
          {mode === 'interactive' ? (
            /* Interactive mode — mic toggle button */
            <>
              <button
                onClick={hook.toggleMic}
                className="w-10 h-10 rounded-full flex items-center justify-center transition-all hover:opacity-80 active:scale-95"
                style={{
                  background: hook.micEnabled ? '#fff' : '#ea4335',
                }}
                title={hook.micEnabled ? 'Mute microphone' : 'Unmute microphone'}
              >
                {hook.micEnabled ? (
                  /* Unmuted — white bg, dark mic icon */
                  <svg className="w-5 h-5" style={{ color: '#3c4043' }} fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 01-3-3V4.5a3 3 0 116 0v8.25a3 3 0 01-3 3z" />
                  </svg>
                ) : (
                  /* Muted — red bg, white mic with slash */
                  <svg className="w-5 h-5" style={{ color: '#fff' }} fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 01-3-3V4.5a3 3 0 116 0v8.25a3 3 0 01-3 3z" />
                    <line x1="3" y1="3" x2="21" y2="21" stroke="currentColor" strokeWidth={2} strokeLinecap="round" />
                  </svg>
                )}
              </button>
              {hook.agentState === 'presentation_complete' && (
                <span className="text-xs ml-2" style={{ color: Z.gray400 }}>
                  Presentation complete
                </span>
              )}
            </>
          ) : (
            /* Button mode — raise hand / resume controls */
            <>
              {/* Presenting — Ask a Question */}
              {(hook.agentState === 'presenting' || hook.agentState === 'audio_only') && (
                <button
                  onClick={hook.raiseHand}
                  className="flex items-center gap-2 px-4 py-1.5 rounded-lg text-xs font-medium transition-all hover:bg-white/10 active:scale-95"
                  style={{ color: Z.gold }}
                >
                  <span>✋</span>
                  Ask a Question
                </button>
              )}

              {/* Loading Q&A */}
              {hook.agentState === 'qa_loading' && (
                <div className="flex items-center gap-2 text-xs font-medium" style={{ color: Z.gold }}>
                  <div className="w-3 h-3 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: `${Z.gold} transparent ${Z.gold} ${Z.gold}` }} />
                  Preparing Q&A...
                </div>
              )}

              {/* Waiting for slide to finish */}
              {hook.agentState === 'qa_pending' && (
                <div className="flex items-center gap-2 text-xs font-medium" style={{ color: Z.gold }}>
                  <span className="animate-pulse">✋</span>
                  Finishing current slide...
                </div>
              )}

              {/* Q&A mode — Continue */}
              {hook.agentState === 'qa' && (
                <button
                  onClick={hook.resumePresentation}
                  className="flex items-center gap-2 px-4 py-1.5 rounded-lg text-xs font-medium transition-all hover:bg-white/10 active:scale-95"
                  style={{ color: Z.gold }}
                >
                  <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                  Continue to Presentation
                </button>
              )}

              {/* Presentation complete */}
              {hook.agentState === 'presentation_complete' && (
                <span className="text-xs font-medium" style={{ color: Z.gray400 }}>
                  Presentation complete — ask any questions
                </span>
              )}
            </>
          )}
        </div>
      </div>

      {/* Animations */}
      <style>{`
        @keyframes lkPulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.6; }
        }
        @keyframes lkBar {
          0%, 100% { transform: scaleY(0.4); }
          50% { transform: scaleY(1); }
        }
      `}</style>
    </div>
  );
};

export default LiveKitAvatarPanel;
